import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaStar, FaQuoteLeft, FaUser } from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await apiService.getTestimonials();
        setTestimonials(response.data || []);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setTestimonials([
          {
            id: 1,
            client_name: 'John Doe',
            client_role: 'CEO',
            client_company: 'TechStart Inc.',
            content: 'Iliyasu delivered an exceptional full-stack solution for our platform. His attention to detail and technical expertise are outstanding.',
            rating: 5,
            featured: true
          },
          {
            id: 2,
            client_name: 'Jane Smith',
            client_role: 'Product Manager',
            client_company: 'Innovate Labs',
            content: 'Working with Iliyasu on our blockchain project was a pleasure. He explained complex concepts clearly and delivered on time.',
            rating: 5,
            featured: true
          },
          {
            id: 3,
            client_name: 'Mike Johnson',
            client_role: 'CTO',
            client_company: 'DataFlow Systems',
            content: 'Iliyasu built our entire backend infrastructure from scratch. The system is robust, scalable, and well-documented.',
            rating: 5
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-4">
            Testimonials
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold">
            What People <span className="gradient-text">Say</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            Feedback from clients and collaborators I've worked with
          </p>
        </motion.div>

        {testimonials.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400">No testimonials available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`glass rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 ${
                  testimonial.featured ? 'border-primary-500/30' : ''
                }`}
              >
                {testimonial.featured && (
                  <div className="inline-block px-3 py-1 text-xs rounded-full bg-primary-500/20 text-primary-400 mb-4">
                    Featured
                  </div>
                )}

                <FaQuoteLeft className="text-primary-400/30 text-2xl mb-3" />
                
                <p className="text-gray-300 leading-relaxed mb-4">
                  {testimonial.content}
                </p>

                <div className="flex items-center gap-3">
                  {testimonial.client_avatar ? (
                    <img 
                      src={testimonial.client_avatar} 
                      alt={testimonial.client_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400">
                      <FaUser size={20} />
                    </div>
                  )}
                  
                  <div>
                    <h4 className="text-white font-semibold">{testimonial.client_name}</h4>
                    <p className="text-gray-400 text-sm">
                      {testimonial.client_role}
                      {testimonial.client_company && ` @ ${testimonial.client_company}`}
                    </p>
                    {testimonial.rating && (
                      <div className="flex gap-0.5 mt-1">
                        {[...Array(5)].map((_, i) => (
                          <FaStar 
                            key={i} 
                            className={`text-xs ${i < testimonial.rating ? 'text-yellow-500' : 'text-gray-600'}`} 
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Testimonials;